import {ErrorBoundary} from 'react-error-boundary';
import {Autoplay, EffectFade} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {diApi} from '@di-shared/api/di';
import {UNIT} from '@di-shared/const';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import useErrorCounter from '@di-shared/features/common/useErrorCounter';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useOpenChargingHabitBottomSheet from '@di-shared/features/home/kr/ev-battery/monitor/useOpenChargingHabitBottomSheet';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface EVBatteryPerformanceStatusProps {
  vin: string;
  puid: string;
}

const NUMBER_OF_COMPONENT = 2;

function EvBatteryChargingHabit(props: EVBatteryPerformanceStatusProps) {
  return (
    <ErrorBoundary fallback={<NoData />}>
      <Inner {...props} />
    </ErrorBoundary>
  );
}

function Inner({vin, puid}: EVBatteryPerformanceStatusProps) {
  const {isError, emitError, isErrorAtIndex} = useErrorCounter(NUMBER_OF_COMPONENT);
  if (isError) {
    throw new ExpectedError();
  }
  return (
    <Swiper
      className="battery-status-box-swiper"
      effect={'fade'}
      slidesPerView={'auto'}
      modules={[Autoplay, EffectFade]}
      autoplay={{delay: 4000, disableOnInteraction: false}}
      observer
      observeParents
      allowTouchMove={false}
    >
      {!isErrorAtIndex(0) && (
        <SwiperSlide className={'battery_status_box'}>
          <ErrorBoundary
            fallback={<></>}
            onError={() => emitError(0)}
          >
            <ChargeRangeStats
              vin={vin}
              puid={puid}
            />
          </ErrorBoundary>
        </SwiperSlide>
      )}
      {!isErrorAtIndex(1) && (
        <SwiperSlide className={'battery_status_box'}>
          <ErrorBoundary
            fallback={<></>}
            onError={() => emitError(1)}
          >
            <LowBatteryAlertCounter
              vin={vin}
              puid={puid}
            />
          </ErrorBoundary>
        </SwiperSlide>
      )}
    </Swiper>
  );
}

function ChargeRangeStats({vin, puid}: EVBatteryPerformanceStatusProps) {
  const {t} = useInferableTranslation();
  const {openChargingHabitBottomSheet} = useOpenChargingHabitBottomSheet({vin, puid});
  const {
    data: {data},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.evBatteryController.getEvChargeSummary), vin, puid],
    queryFn: () => diApi.evBatteryController.getEvChargeSummary({vin, puid}),
  });

  return (
    <a
      role={'button'}
      className={'battery_status_link'}
      onClick={openChargingHabitBottomSheet}
    >
      <div className="status-inner">
        <span className="ic ic_filled_ev_charging"></span>
        <div className="battery_info">
          <p className="battery_status">{`${Math.round(data.avgMinSoc)}-${Math.round(data.avgMaxSoc)}${UNIT.PERCENT}`}</p>
          <p className="battery_desc">{t('T30455')}</p>
        </div>
      </div>
      <span className="battery_title">{t('T30456')}</span>
    </a>
  );
}

function LowBatteryAlertCounter({vin, puid}: EVBatteryPerformanceStatusProps) {
  const {t} = useInferableTranslation();
  const {openChargingHabitBottomSheet} = useOpenChargingHabitBottomSheet({vin, puid});
  const {
    data: {data},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.evBatteryController.getEvChargeHistory), vin, puid],
    queryFn: () => diApi.evBatteryController.getEvChargeHistory({vin, puid}),
  });
  const LowBatteryThreshold = 10;
  const LowBatteryCount = data.chargedDays.reduce((acc, cur) => acc + cur.lowSocAlert, 0);

  if (LowBatteryCount <= 0) {
    throw new ExpectedError();
    /**
     * @link https://jira.hmg-corp.io/browse/ICTQMSCHE-14776
     * 0회일 경우 의도적으로 에러를 반환하게 하여 스와이퍼에서 LowBatteryAlertCounter를 뺌
     */
  }

  return (
    <a
      role={'button'}
      className={'battery_status_link'}
      onClick={openChargingHabitBottomSheet}
    >
      <div className="status-inner">
        <span className="ic ic_filled_caution"></span>
        <div className="battery_info">
          <p className="battery_status">{t('T10014_$count', {count: LowBatteryCount})}</p>
          <p className="battery_desc">{t('T30464_percent', {percent: LowBatteryThreshold})}</p>
        </div>
      </div>
      <span className="battery_title">{t('T30456')}</span>
    </a>
  );
}

function NoData() {
  const {t} = useInferableTranslation();
  return (
    <div className="battery_status_box">
      <a className="battery_status_link">
        <div className="loading_lottie">
          <LottieSelector
            id="img_json_loading_black_60f"
            kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
            kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
            genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
            genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
            loop={true}
            autoplay={true}
            style={{width: 24, height: 24}}
          />
        </div>
        <div className="battery_info">
          <p className="battery_status">{t('T20082')}</p>
        </div>
        <span className="battery_title">{t('T30456')}</span>
      </a>
    </div>
  );
}

export default EvBatteryChargingHabit;
